<template>
  <div class="bg-purple-50 rounded-lg p-4 space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h4 class="text-sm font-medium text-purple-900">💡 Mood Light (Pixels 0-9)</h4>
        <p class="text-xs text-purple-700">Rainbow or static color lighting</p>
      </div>
      <button
        @click="toggleMoodLight"
        :disabled="!isOnline"
        :class="[
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
          currentMoodLightEnabled 
            ? 'bg-purple-600' 
            : 'bg-gray-200',
          !isOnline ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        ]"
      >
        <span
          :class="[
            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
            currentMoodLightEnabled ? 'translate-x-6' : 'translate-x-1'
          ]"
        />
      </button>
    </div>

    <div v-if="currentMoodLightEnabled" class="space-y-3">
      <!-- Mode Selection -->
      <div>
        <label class="block text-xs font-medium text-purple-800 mb-1">Mode</label>
        <div class="flex space-x-2">
          <button
            v-for="mode in moodLightModes"
            :key="mode.value"
            @click="setMoodLightMode(mode.value)"
            :disabled="!isOnline"
            :class="[
              'px-3 py-1 text-xs font-medium rounded',
              currentMoodLightMode === mode.value
                ? 'bg-purple-600 text-white'
                : 'bg-white text-purple-700 border border-purple-200 hover:bg-purple-50',
              !isOnline ? 'opacity-50 cursor-not-allowed' : ''
            ]"
          >
            {{ mode.icon }} {{ mode.label }}
          </button>
        </div>
      </div>

      <!-- Static Color Picker (only for static mode) -->
      <div v-if="currentMoodLightMode === 'static'" class="grid grid-cols-3 gap-2">
        <div>
          <label class="block text-xs font-medium text-purple-800 mb-1">Red</label>
          <input
            type="range"
            min="0"
            max="255"
            v-model.number="localMoodLightColor.r"
            @input="updateMoodLightColor"
            :disabled="!isOnline"
            class="w-full h-2 bg-red-200 rounded-lg"
          >
          <span class="text-xs text-purple-700">{{ localMoodLightColor.r }}</span>
        </div>
        <div>
          <label class="block text-xs font-medium text-purple-800 mb-1">Green</label>
          <input
            type="range"
            min="0"
            max="255"
            v-model.number="localMoodLightColor.g"
            @input="updateMoodLightColor"
            :disabled="!isOnline"
            class="w-full h-2 bg-green-200 rounded-lg"
          >
          <span class="text-xs text-purple-700">{{ localMoodLightColor.g }}</span>
        </div>
        <div>
          <label class="block text-xs font-medium text-purple-800 mb-1">Blue</label>
          <input
            type="range"
            min="0"
            max="255"
            v-model.number="localMoodLightColor.b"
            @input="updateMoodLightColor"
            :disabled="!isOnline"
            class="w-full h-2 bg-blue-200 rounded-lg"
          >
          <span class="text-xs text-purple-700">{{ localMoodLightColor.b }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  device: {
    type: Object,
    required: true
  },
  isOnline: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['update-desired'])

// Local state
const localMoodLightColor = ref({ r: 255, g: 100, b: 100 })
let ledUpdateTimer = null

// LED configuration data
const moodLightModes = [
  { value: 'rainbow', label: 'Rainbow', icon: '🌈' },
  { value: 'static', label: 'Static Color', icon: '🎨' }
]

// Computed properties
const currentLedConfig = computed(() =>
  props.device.shadow?.desired?.ledConfig || {}
)

const currentMoodLightEnabled = computed({
  get: () => currentLedConfig.value?.moodLight?.enabled || false,
  set: (value) => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.moodLight.enabled = value
    updateDesiredState({ ledConfig: completeConfig })
  }
})

const currentMoodLightMode = computed(() => 
  currentLedConfig.value?.moodLight?.mode || (currentLedConfig.value?.moodLight?.rainbow ? 'rainbow' : 'static')
)

// Helper function to create complete LED config objects
const createCompleteLedConfig = () => {
  const current = currentLedConfig.value || {}
  return {
    moodLight: {
      enabled: current.moodLight?.enabled || false,
      mode: current.moodLight?.mode || (current.moodLight?.rainbow ? 'rainbow' : 'static'),
      color: current.moodLight?.color || localMoodLightColor.value
    },
    progressBar: {
      enabled: current.progressBar?.enabled || false,
      startDateTime: current.progressBar?.startDateTime || '',
      endDateTime: current.progressBar?.endDateTime || '',
      message: current.progressBar?.message || '',
      color: current.progressBar?.color || { r: 0, g: 255, b: 0 },
      backgroundColor: current.progressBar?.backgroundColor || { r: 20, g: 20, b: 20 },
      effect: current.progressBar?.effect || 'solid'
    }
  }
}

// Watch for LED configuration changes
watch(() => currentLedConfig.value?.moodLight?.color, (newColor) => {
  if (newColor) {
    localMoodLightColor.value = { ...newColor }
  }
}, { immediate: true, deep: true })

// Methods
const updateDesiredState = (newState) => {
  emit('update-desired', props.device.deviceId, newState)
}

const updateMoodLightColor = () => {
  if (!props.isOnline) return
  clearTimeout(ledUpdateTimer)
  ledUpdateTimer = setTimeout(() => {
    const completeConfig = createCompleteLedConfig()
    completeConfig.moodLight.color = { ...localMoodLightColor.value }
    updateDesiredState({ ledConfig: completeConfig })
  }, 300)
}

const toggleMoodLight = () => {
  if (!props.isOnline) return
  currentMoodLightEnabled.value = !currentMoodLightEnabled.value
}

const setMoodLightMode = (mode) => {
  if (!props.isOnline) return
  const completeConfig = createCompleteLedConfig()
  completeConfig.moodLight.mode = mode
  updateDesiredState({ ledConfig: completeConfig })
}
</script>